import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { Input } from 'src/design/atoms/Input';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Radio } from 'src/design/atoms/Radio';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import { useFormik } from 'formik';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import {
  FileResponse,
  JobDto,
  NotificationCompositionRequest,
  ResourceAttachmentDto,
  RfiDto,
  RfiExternalUpdateDto,
  RfiRequestDto,
  UserDto,
  WorkflowStepDto,
} from 'src/api/FiretrolApiClient';
import {
  usePreviewRfiPdf,
  useRotateRfiExternalAccess,
  useSaveRfi,
} from 'src/api/queries/rfis/useRfiMutations';
import { useSaveExternalRfi } from 'src/api/queries/rfis/useSaveExternalRfi';
import {
  useUpdateAttachmentComment,
  useUploadTempAttachment,
} from 'src/api/queries/workflowModules/useAttachmentMutations';
import { FileDrop } from 'src/design/organisms/FileDrop/FileDrop';
import { applyAttachmentChange } from 'src/design/organisms/attachments/attachmentEdits';
import {
  IAttachedFile,
  IAttachmentChange,
} from 'src/design/organisms/attachments/attachmentTypes';
import { JobUserSelect } from 'src/app/selectors/JobUserSelectSingle';
import { ContactPicker } from 'src/app/selectors/ContactPicker';
import { useWorkflowState } from 'src/app/workflows/WorkflowButtons/useWorkflowState';
import { isStepEditable } from 'src/app/workflows/isStepEditable';
import { useRevokeExternalLinks } from 'src/app/workflows/useRevokeExternalLinks';
import { RFIDocumentPicker } from 'src/routes/Jobs/RFIs/components/RFIDocumentPicker';
import { useNotificationComposer } from 'src/hooks/useNotificationComposer';
import { WorkflowFormFooter } from 'src/app/workflows/WorkflowFormFooter';
import { shouldOpenTransitionComposer } from 'src/app/workflows/shouldOpenTransitionComposer';
import { useAuth } from 'src/hooks/useAuth';
import { useWorkflowSubmission } from 'src/hooks/useWorkflowSubmission';
import {
  characterCount,
  FIELD_LIMITS,
  getErrorMessage,
  logger,
  showErrorToast,
  showToast,
} from 'src/utils';
import { PaperStack } from 'src/design/atoms/PaperStack';
import { DatePicker } from 'src/design/atoms/DatePicker';
import * as Yup from 'yup';
import { IContact } from 'src/api/models/Organization';
import { contactDtoToIContact } from 'src/app/workflows/contactDtoToIContact';
import { parseExternalContact } from 'src/app/workflows/parseExternalContact';
import { useWorkflowFormValidation } from 'src/app/workflows/useWorkflowFormValidation';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';

interface FormTarget {
  name: string;
  value: string;
}

const RFI_FIELD_LABELS: Record<string, string> = {
  needDate: 'Need Date',
  question: 'Question',
  recommendation: 'Recommendation',
  responseToQuestions: 'Response to Question',
  responseToImpacts: 'Response to Impact',
  impactNotes: 'Notes',
  externalRfiNumber: 'RFI # (External)',
  fromCompany: 'Company Name',
  responseFrom: 'Response From',
  fromUserId: 'From',
  submittedToContactId: 'To',
  costDollars: 'Cost',
  costDays: 'Schedule (days)',
  impact: 'Potential Impact',
};

const validationSchema = Yup.object().shape({
  // rfiNumber carries no rule: it is server-generated and the input is read-only.
  needDate: Yup.date()
    .nullable()
    .typeError('Need date is invalid')
    .when('id', {
      is: (id?: string) => !id,
      then: (schema) => schema.required('Need date is required'),
    })
    .label('Need Date'),
  question: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.narrative,
      `Question must be ${FIELD_LIMITS.narrative} characters or fewer`,
    ),
  recommendation: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.narrative,
      `Recommendation must be ${FIELD_LIMITS.narrative} characters or fewer`,
    ),
  responseToQuestions: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.narrative,
      `Response to Question must be ${FIELD_LIMITS.narrative} characters or fewer`,
    ),
  responseToImpacts: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.narrative,
      `Response to Impact must be ${FIELD_LIMITS.narrative} characters or fewer`,
    ),
  impactNotes: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.narrative,
      `Notes must be ${FIELD_LIMITS.narrative} characters or fewer`,
    ),
  externalRfiNumber: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.singleLine,
      `RFI # (External) must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    ),
  fromCompany: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.singleLine,
      `Company Name must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .when('id', {
      is: (id?: string) => !id,
      then: (schema) => schema.required('Company name is required'),
    }),
  fromUserId: Yup.number()
    .nullable()
    .when('id', {
      is: (id?: string) => !id,
      then: (schema) => schema.required('From is required'),
    }),
  submittedToContactId: Yup.string()
    .nullable()
    .when('id', {
      is: (id?: string) => !id,
      then: (schema) => schema.required('A To contact is required'),
    }),
  responseFrom: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.singleLine,
      `Response From must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    ),
});

/** Which slice of the form to render; omit to render the full single-column form (create flow). */
export type RFIFormSection =
  | 'details'
  | 'attachments'
  | 'notifications'
  | 'history'
  | 'email';

// Extended form values that track pending attachment edits alongside the persisted RFI.
export interface RfiFormValues extends RfiDto {
  filesToAdd: ResourceAttachmentDto[];
  filesToRemove: ResourceAttachmentDto[];
  responsesToAdd: ResourceAttachmentDto[];
  responsesToRemove: ResourceAttachmentDto[];
}

interface IRFIFormProps {
  initialValues: RfiDto;
  onSaved?: (rfi: RfiDto) => void;
  selectedJob?: JobDto;
  isExternal?: boolean;
  /** In ViewRFI the form is split across tabs; this selects the visible slice. Omit for the single-column create flow. */
  activeSection?: RFIFormSection;
  jobUsers?: UserDto[];
  /** Content for non-form tabs (notifications, history, email) rendered above the persistent footer. */
  children?: React.ReactNode;
}

interface IPendingTransition {
  status: string;
  stepIndex: number;
  stepLabel: string;
}

export const RFIForm: React.FC<IRFIFormProps> = ({
  initialValues,
  onSaved,
  selectedJob,
  isExternal,
  activeSection,
  jobUsers = [],
  children,
}) => {
  const { user, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const externalToken = searchParams.get('token');
  const [externalError, setExternalError] = React.useState<string | null>(null);

  const formik = useFormik<RfiFormValues>({
    initialValues: {
      ...initialValues,
      filesToAdd: [],
      filesToRemove: [],
      responsesToAdd: [],
      responsesToRemove: [],
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async () => {},
  });
  const { values, setFieldValue } = formik;

  const { currentStep } = useWorkflowState(
    values.workflow ?? null,
    values.stepIndex ?? -1,
  );

  const [submittedToContact, setSubmittedToContact] =
    React.useState<IContact | null>(
      contactDtoToIContact(
        initialValues.submittedToContact,
        initialValues.submittedToContactId,
      ),
    );

  const [attentionContact, setAttentionContact] =
    React.useState<IContact | null>(
      contactDtoToIContact(
        initialValues.attentionContact,
        initialValues.attentionContactId,
      ),
    );

  const composer = useNotificationComposer(
    'rfi',
    selectedJob?.id ?? 0,
    values.id ?? '',
  );
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [pendingTransition, setPendingTransition] =
    React.useState<IPendingTransition | null>(null);

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewFile, setPreviewFile] = React.useState<FileResponse | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const previewUrl = usePdfBlobUrl(previewFile?.data);

  const saveRfi = useSaveRfi();
  const saveExternal = useSaveExternalRfi();
  const previewRfiPdf = usePreviewRfiPdf();
  const rotateExternalAccess = useRotateRfiExternalAccess();
  const uploadTempAttachment = useUploadTempAttachment();
  const downloadAttachment = useResolveAttachmentUrl();
  const updateAttachmentComment = useUpdateAttachmentComment();

  const revoker = useRevokeExternalLinks<RfiDto>({
    rotate: () =>
      rotateExternalAccess.mutateAsync({
        jobId: selectedJob?.id ?? 0,
        id: values.id ?? '',
      }),
    onRevoked: (dto) => {
      formik.resetForm({
        values: {
          ...values,
          attachments: dto.attachments ?? values.attachments,
          responseAttachments:
            dto.responseAttachments ?? values.responseAttachments,
        },
      });
      onSaved?.(dto);
    },
    logLabel: 'Failed to revoke RFI external links',
  });

  const buildRequest = (
    overrides: Partial<RfiRequestDto> = {},
  ): RfiRequestDto => ({
    id: values.id || undefined,
    costDays: values.costDays,
    costDollars: values.costDollars,
    externalRfiNumber: values.externalRfiNumber,
    from: values.from,
    fromCompany: values.fromCompany,
    impact: values.impact,
    impactNotes: values.impactNotes,
    question: values.question,
    recommendation: values.recommendation,
    responseToImpacts: values.responseToImpacts,
    responseToQuestions: values.responseToQuestions,
    responseFrom: values.responseFrom,
    responseDate: values.responseDate,
    status: values.status,
    stepIndex: values.stepIndex,
    workflowId: values.workflowId,
    fromUserId: values.fromUserId,
    reporterUserId: values.reporterUserId,
    needDate: values.needDate,
    externalContact: values.externalContact,
    submittedToContactId: values.submittedToContactId,
    attentionContactId: values.attentionContactId,
    filesToAdd: values.filesToAdd?.map((f) => ({ id: f.id })),
    filesToRemove: values.filesToRemove?.map((f) => ({ id: f.id })),
    responsesToAdd: values.responsesToAdd?.map((f) => ({ id: f.id })),
    responsesToRemove: values.responsesToRemove?.map((f) => ({ id: f.id })),
    saveOnly: false,
    notification: undefined,
    ...overrides,
  });

  const {
    submitWorkflow,
    voidWorkflow,
    saveOnly,
    error: hookError,
    fieldErrors,
    isSubmitting,
  } = useWorkflowSubmission<RfiFormValues, RfiRequestDto>(
    values,
    selectedJob?.id ?? 0,
    {
      mutations: { save: saveRfi },
      toRequest: (_vals, overrides) =>
        buildRequest({
          status: overrides?.status,
          stepIndex: overrides?.stepIndex,
          saveOnly: overrides?.saveOnly ?? false,
          notification: overrides?.notification,
        }),
      entityName: 'RFI',
      toast: { getEntityNumber: (r) => r.rfiNumber ?? '' },
    },
    {
      onSaved,
      setValues: (dto) => {
        formik.resetForm({
          values: {
            ...values,
            ...dto,
            attachments: dto.attachments ?? values.attachments,
            responseAttachments:
              dto.responseAttachments ?? values.responseAttachments,
            filesToAdd: [],
            filesToRemove: [],
            responsesToAdd: [],
            responsesToRemove: [],
            workflow: dto.workflow ?? values.workflow,
          },
        });
      },
    },
  );

  const error = hookError || externalError;

  const { passesValidation, fieldError } =
    useWorkflowFormValidation<RfiFormValues>(
      formik,
      fieldErrors,
      RFI_FIELD_LABELS,
    );

  const handleSaveOnly = async () => {
    if (!(await passesValidation())) return;
    await saveOnly();
  };

  const openComposerWithPreview = async (
    eventType: string,
    onFailure?: () => void,
  ): Promise<void> => {
    setComposerOpen(true);
    const preview = await composer.loadPreview(eventType);
    if (!preview) {
      setComposerOpen(false);
      onFailure?.();
      showErrorToast(
        'Unable to load the notification preview. Please try again.',
      );
    }
  };

  const setValue = ({ target }: { target: FormTarget }) => {
    setFieldValue(target.name, target.value);
  };

  const onSubmitClick = async (status: string, nextStepIndex: number) => {
    if (!(await passesValidation())) return;

    // External contractor (unauthenticated, token link) responds through the RFI's external step.
    if (currentStep?.isExternalStep && !user) {
      setExternalError(null);
      try {
        const externalRequest: RfiExternalUpdateDto = {
          responseToImpacts: values.responseToImpacts,
          responseToQuestions: values.responseToQuestions,
          responseFrom: values.responseFrom,
          status,
          stepIndex: nextStepIndex,
        };
        const result = await saveExternal.mutateAsync({
          rfiId: values.id,
          token: externalToken,
          jobId: selectedJob?.id,
          request: externalRequest,
        });
        await formik.setValues({
          ...result,
          filesToAdd: [],
          filesToRemove: [],
          responsesToAdd: [],
          responsesToRemove: [],
        });
        onSaved?.(result);
      } catch (err: unknown) {
        setExternalError(getErrorMessage(err));
      }
      return;
    }

    const currentStepIndex = values.stepIndex ?? -1;

    // Revise returns an active RFI to Draft as "More Information Needed" and
    // persists as saveOnly (no email, no log row). Draft→-1 while creating
    // (currentStepIndex === -1) is an ordinary draft save and falls through.
    if (nextStepIndex === -1 && status === 'Draft' && currentStepIndex >= 0) {
      await submitWorkflow('More Information Needed', -1, true);
      return;
    }

    const isForward = nextStepIndex === currentStepIndex + 1;
    const targetStep = values.workflow?.steps?.[nextStepIndex] ?? null;

    if (
      isForward &&
      !!values.id &&
      targetStep?.isExternalStep &&
      !values.submittedToContactId &&
      !parseExternalContact(values.externalContact)
    ) {
      showErrorToast('Select a To contact before submitting this RFI.');
      return;
    }

    // Both RFI steps (Submitted external, Responded createPdf) open the composer;
    // internal moves submit directly. The composer's Skip path covers internal RFIs.
    if (
      shouldOpenTransitionComposer({
        isForward,
        hasId: !!values.id,
        targetStep,
      })
    ) {
      setPendingTransition({
        status,
        stepIndex: nextStepIndex,
        stepLabel: targetStep.label ?? '',
      });
      await openComposerWithPreview(`StepAdvanced:${nextStepIndex}`, () =>
        setPendingTransition(null),
      );
      return;
    }

    await submitWorkflow(status, nextStepIndex);
  };

  const submitTransition = async (request: NotificationCompositionRequest) => {
    if (!pendingTransition) return;
    try {
      const result = await submitWorkflow(
        pendingTransition.status,
        pendingTransition.stepIndex,
        false,
        { notification: request },
      );
      if (!result) {
        showErrorToast('Unable to submit the RFI. Please try again.');
        return;
      }
      setComposerOpen(false);
      setPendingTransition(null);
    } catch (err: unknown) {
      logger.error('Failed to submit RFI transition', { err });
      showErrorToast(getErrorMessage(err));
    }
  };

  const cancelTransition = () => {
    setComposerOpen(false);
    setPendingTransition(null);
    showToast('Submission canceled — status unchanged');
  };

  const handlePreviewPdf = async () => {
    if (!selectedJob || !values.id) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    const file = await previewRfiPdf
      .mutateAsync({ jobId: selectedJob.id, id: values.id })
      .catch(() => undefined);
    if (file === undefined) setPreviewOpen(false);
    else setPreviewFile(file);
    setPreviewLoading(false);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleCommentSave = async (file: IAttachedFile, comment: string) => {
    const updated = await updateAttachmentComment
      .mutateAsync({
        jobId: selectedJob?.id ?? 0,
        attachmentId: file.id,
        comment,
      })
      .catch(() => null);
    if (!updated) return;
    formik.setValues((v) => ({
      ...v,
      attachments: (v.attachments ?? []).map((a) =>
        a.id === file.id ? { ...a, comment: updated.comment ?? comment } : a,
      ),
    }));
  };

  const handleDocumentAttached = (attachment: ResourceAttachmentDto) => {
    formik.setValues((v) => ({
      ...v,
      attachments: [...(v.attachments ?? []), attachment],
      filesToAdd: [...v.filesToAdd, attachment],
    }));
  };

  const applyAttachmentEdit = (
    files: IAttachedFile[],
    change: IAttachmentChange,
  ) => {
    const edits = applyAttachmentChange(
      { added: values.filesToAdd, removed: values.filesToRemove },
      change,
    );
    formik.setValues((v) => ({
      ...v,
      attachments: files as ResourceAttachmentDto[],
      filesToAdd: edits.added as ResourceAttachmentDto[],
      filesToRemove: edits.removed as ResourceAttachmentDto[],
    }));
  };

  const applyResponseEdit = (
    files: IAttachedFile[],
    change: IAttachmentChange,
  ) => {
    const edits = applyAttachmentChange(
      { added: values.responsesToAdd, removed: values.responsesToRemove },
      change,
    );
    formik.setValues((v) => ({
      ...v,
      responseAttachments: files as ResourceAttachmentDto[],
      responsesToAdd: edits.added as ResourceAttachmentDto[],
      responsesToRemove: edits.removed as ResourceAttachmentDto[],
    }));
  };

  if (!selectedJob) return null;

  // Anonymous holders of an external link may never edit the details.
  const isDisabled =
    !user || !isStepEditable({ isAdmin, currentStep, status: values.status });
  const assignee = values.reporter
    ? values.reporter.fullName
    : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
  const showResponse = (values.stepIndex ?? -1) >= 0;
  // Anonymous holders of an external link may only answer on the external step.
  const canEditResponse =
    values.status !== 'Voided' && (!!user || !!currentStep?.isExternalStep);
  const customerNumber = selectedJob.customerNumber?.trim() ?? '';
  const externalContact = parseExternalContact(values.externalContact);

  const showDetails = !activeSection || activeSection === 'details';
  const showAttachments = !activeSection || activeSection === 'attachments';

  const attachmentsSection = (
    <PaperStack>
      <Group justify="space-between">
        <Title order={4}>Attachments</Title>
        {values.id &&
          !isExternal &&
          (values.attachments ?? []).some((f) => f.externalUrl) && (
            <SecondaryButton onClick={revoker.openModal}>
              Revoke links
            </SecondaryButton>
          )}
      </Group>
      <RFIDocumentPicker
        jobId={selectedJob.id}
        onAttached={handleDocumentAttached}
      />
      <FileDrop
        disabled={currentStep?.isLastStep || values.status === 'Voided'}
        files={values.attachments ?? []}
        onChange={applyAttachmentEdit}
        onCommentSave={handleCommentSave}
        onCopyLink={revoker.copyLink}
        uploadFile={(file) =>
          uploadTempAttachment.mutateAsync({ typeId: 'rfis', file })
        }
        downloadFile={(id) => downloadAttachment.mutateAsync(id)}
      />
    </PaperStack>
  );

  return (
    <Stack>
      {showDetails && (
        <>
          <PaperStack>
            <Group justify="space-between">
              <Title order={4}>Details</Title>
              {values.id && (
                <SecondaryButton onClick={handlePreviewPdf}>
                  Preview PDF
                </SecondaryButton>
              )}
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Stack>
                <TextInput
                  label="Job"
                  id="job"
                  value={selectedJob.name}
                  disabled
                />
                <TextInput
                  label="Client"
                  id="client"
                  value={`${selectedJob.customerName}`}
                  disabled
                />
                <TextInput
                  label="Assignee"
                  id="reporter"
                  value={assignee}
                  disabled
                />
              </Stack>
              <Stack>
                <TextInput
                  label="Number"
                  id="number"
                  name="rfiNumber"
                  value={values.rfiNumber ?? ''}
                  disabled
                  placeholder="Auto Generated"
                />
                <TextInput
                  label="RFI # (External)"
                  id="externalRfiNumber"
                  name="externalRfiNumber"
                  disabled={isDisabled}
                  value={
                    isDisabled && values.externalRfiNumber === ''
                      ? 'N/A'
                      : values.externalRfiNumber ?? ''
                  }
                  onChange={setValue}
                  onBlur={formik.handleBlur}
                  maxLength={FIELD_LIMITS.singleLine}
                  error={
                    formik.touched.externalRfiNumber &&
                    formik.errors.externalRfiNumber
                  }
                  placeholder="ex: RFI-000149 (RFI #147)"
                />
                <TextInput
                  label="Status"
                  id="status"
                  name="status"
                  value={values.status ?? ''}
                  disabled
                  placeholder="N/A"
                />
              </Stack>
            </SimpleGrid>
            {isAdmin && (
              <TextInput
                label="Assigned Workflow"
                description="(Admin Only)"
                id="workflow"
                name="workflow"
                value={values.workflow?.name}
                disabled
                mt="md"
              />
            )}
          </PaperStack>
          <PaperStack>
            <Title order={4}>Summary</Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Stack>
                <ContactPicker
                  label="To"
                  required
                  error={fieldError('submittedToContactId')}
                  disabled={isDisabled}
                  allowCreate
                  customerNumber={customerNumber}
                  customerName={selectedJob.customerName ?? ''}
                  initialValue={submittedToContact}
                  onChange={(contact) => {
                    setSubmittedToContact(contact);
                    setFieldValue(
                      'submittedToContactId',
                      contact?.id ?? undefined,
                    );
                  }}
                />
                <ContactPicker
                  label="Attention"
                  disabled={isDisabled}
                  allowCreate
                  customerNumber={customerNumber}
                  customerName={selectedJob.customerName ?? ''}
                  initialValue={attentionContact}
                  onChange={(contact) => {
                    setAttentionContact(contact);
                    setFieldValue(
                      'attentionContactId',
                      contact?.id ?? undefined,
                    );
                  }}
                />
              </Stack>
              <Stack>
                {isExternal && values.fromUser ? (
                  <TextInput
                    label="From"
                    id="from"
                    name="from"
                    value={values.fromUser.fullName}
                    disabled
                  />
                ) : (
                  <Input.Wrapper
                    label="From"
                    withAsterisk
                    error={fieldError('fromUserId')}
                  >
                    <JobUserSelect
                      hideLabel
                      small
                      hideRole
                      selectedUserId={formik.values.fromUserId}
                      onChange={(value) => {
                        if (value) {
                          formik.setFieldValue('fromUserId', value.userId);
                          formik.setFieldValue(
                            'fromCompany',
                            value.user.companyName,
                          );
                        } else {
                          formik.setFieldValue('fromUserId', -1);
                          formik.setFieldValue('fromCompany', '');
                        }
                      }}
                      disabled={isDisabled}
                    />
                  </Input.Wrapper>
                )}

                <TextInput
                  label="Company Name"
                  id="fromCompany"
                  disabled={isDisabled}
                  name="fromCompany"
                  value={
                    isDisabled && values.fromCompany === ''
                      ? 'N/A'
                      : values.fromCompany ?? ''
                  }
                  style={{ marginTop: 2 }}
                  onChange={setValue}
                  onBlur={formik.handleBlur}
                  maxLength={FIELD_LIMITS.singleLine}
                  error={fieldError('fromCompany')}
                  placeholder="What's the company name?"
                  withAsterisk
                />
              </Stack>
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 2 }} mt="md">
              <DatePicker
                label="Need Date"
                value={values.needDate}
                onChange={(value) => {
                  setFieldValue('needDate', value ?? null);
                }}
                name="needDate"
                withAsterisk
                error={fieldError('needDate')}
              />
            </SimpleGrid>
          </PaperStack>
          <PaperStack>
            <Title order={4}>Questions</Title>
            <Input.Wrapper
              description={characterCount(
                values.question,
                FIELD_LIMITS.narrative,
              )}
              error={formik.touched.question && formik.errors.question}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            >
              <ReactQuill
                className={isDisabled ? 'quill-disabled' : ''}
                theme="snow"
                value={values.question}
                readOnly={isDisabled}
                onChange={(value) => {
                  setFieldValue('question', value);
                }}
              />
            </Input.Wrapper>
            <Title order={5}>Recommendation:</Title>
            <Input.Wrapper
              description={characterCount(
                values.recommendation,
                FIELD_LIMITS.narrative,
              )}
              error={
                formik.touched.recommendation && formik.errors.recommendation
              }
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            >
              <ReactQuill
                className={isDisabled ? 'quill-disabled' : ''}
                theme="snow"
                value={values.recommendation}
                readOnly={isDisabled}
                onChange={(value) => {
                  setFieldValue('recommendation', value);
                }}
              />
            </Input.Wrapper>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <Radio.Group
                label="Potential Impact"
                name="impact"
                onChange={(value) =>
                  setValue({ target: { name: 'impact', value } })
                }
                value={values.impact ?? ''}
                error={fieldError('impact')}
              >
                <Group>
                  <Radio label="Yes" value="yes" disabled={isDisabled} />
                  <Radio label="No" value="no" disabled={isDisabled} />
                  <Radio label="TBD" value="tbd" disabled={isDisabled} />
                </Group>
              </Radio.Group>
              <NumberInput
                label="Cost"
                disabled={isDisabled}
                placeholder="$0.00"
                prefix="$"
                value={values.costDollars ? parseFloat(values.costDollars) : ''}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator=","
                allowNegative={false}
                styles={{ input: { textAlign: 'right' } }}
                onChange={(value) => {
                  setFieldValue('costDollars', value?.toString() ?? '');
                }}
                error={fieldError('costDollars')}
              />
              <NumberInput
                label="Schedule (days)"
                disabled={isDisabled}
                placeholder="0"
                suffix=" days"
                value={values.costDays ? parseInt(values.costDays, 10) : ''}
                allowDecimal={false}
                allowNegative={false}
                styles={{ input: { textAlign: 'right' } }}
                onChange={(value) => {
                  setFieldValue('costDays', value?.toString() ?? '');
                }}
                error={fieldError('costDays')}
              />
            </SimpleGrid>
            <Textarea
              label="Notes"
              id="impactNotes"
              name="impactNotes"
              value={
                isDisabled && values.impactNotes === ''
                  ? 'N/A'
                  : values.impactNotes ?? ''
              }
              disabled={isDisabled}
              onChange={setValue}
              onBlur={formik.handleBlur}
              autosize
              minRows={3}
              maxLength={FIELD_LIMITS.narrative}
              description={characterCount(
                values.impactNotes,
                FIELD_LIMITS.narrative,
              )}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              error={formik.touched.impactNotes && formik.errors.impactNotes}
            />
          </PaperStack>
        </>
      )}

      {showAttachments && attachmentsSection}

      {showDetails && showResponse && (
        <Stack>
          <Divider />
          <PaperStack>
            <Title order={4}>RFI Response</Title>
            <Title order={5}>Response to Question</Title>
            <Input.Wrapper
              description={characterCount(
                values.responseToQuestions,
                FIELD_LIMITS.narrative,
              )}
              error={
                formik.touched.responseToQuestions &&
                formik.errors.responseToQuestions
              }
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            >
              <ReactQuill
                className={!canEditResponse ? 'quill-disabled' : ''}
                readOnly={!canEditResponse}
                theme="snow"
                value={values.responseToQuestions ?? ''}
                onChange={(value) => {
                  setFieldValue('responseToQuestions', value);
                }}
              />
            </Input.Wrapper>
            <Title order={5}>Response to Impact</Title>
            <Input.Wrapper
              description={characterCount(
                values.responseToImpacts,
                FIELD_LIMITS.narrative,
              )}
              error={
                formik.touched.responseToImpacts &&
                formik.errors.responseToImpacts
              }
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            >
              <ReactQuill
                className={!canEditResponse ? 'quill-disabled' : ''}
                readOnly={!canEditResponse}
                theme="snow"
                value={values.responseToImpacts ?? ''}
                onChange={(value) => {
                  setFieldValue('responseToImpacts', value);
                }}
              />
            </Input.Wrapper>
            <TextInput
              label="Response From"
              id="responseFrom"
              name="responseFrom"
              disabled={!canEditResponse}
              onChange={setValue}
              onBlur={formik.handleBlur}
              maxLength={FIELD_LIMITS.singleLine}
              error={fieldError('responseFrom')}
              placeholder="Enter your name..."
              value={values.responseFrom ?? ''}
            />
            <PaperStack stackProps={{ gap: 'sm' }}>
              <Title order={4} mb="sm">
                Upload Response
              </Title>
              <FileDrop
                disabled={!canEditResponse}
                files={values.responseAttachments ?? []}
                onChange={applyResponseEdit}
                uploadFile={(file) =>
                  uploadTempAttachment.mutateAsync({ typeId: 'rfis', file })
                }
                downloadFile={(id) => downloadAttachment.mutateAsync(id)}
              />
            </PaperStack>
          </PaperStack>
        </Stack>
      )}

      {showDetails && externalContact && (
        <PaperStack>
          <Title order={4}>Client Contact</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Name"
              id="name"
              value={`${externalContact.first_name ?? ''} ${
                externalContact.last_name ?? ''
              }`}
              disabled
            />
            <TextInput
              label="Email"
              id="email"
              name="email"
              value={externalContact.email ?? ''}
              disabled
            />
          </SimpleGrid>
        </PaperStack>
      )}

      {children}

      <WorkflowFormFooter
        dirty={formik.dirty}
        showButtons={values.status !== 'Voided'}
        error={error}
        onSubmitClick={onSubmitClick}
        workflow={values.workflow ?? null}
        onSaveOnlyClick={handleSaveOnly}
        onVoidClick={voidWorkflow}
        stepIndex={values.stepIndex ?? -1}
        composerOpen={composerOpen}
        onComposerClose={cancelTransition}
        preview={composer.preview}
        jobUsers={jobUsers}
        customerNumber={customerNumber}
        composerSubmitting={isSubmitting}
        composerTitle={`${
          pendingTransition?.stepLabel ?? 'RFI'
        } — notification`}
        attachPdfLabel="Attach RFI PDF"
        onSend={submitTransition}
        onSkip={submitTransition}
        onReloadPreview={composer.reloadPreview}
        previewOpen={previewOpen}
        onPreviewClose={closePreview}
        pdfTitle="RFI PDF"
        previewUrl={previewUrl}
        previewLoading={previewLoading}
        revokeOpen={revoker.modalOpen}
        onRevokeClose={revoker.closeModal}
        onRevokeConfirm={revoker.revoke}
        revoking={revoker.revoking}
        revokeEntityNoun="RFI"
        revokeItemsNoun="attachments"
      />
    </Stack>
  );
};
